import {
  FC,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useLocation,
} from 'react-router-dom';
import { z } from 'zod';
import { useApi, useRequiredContext } from '../hooks';
import {
  formatOrderNumbers,
  getValueLabelFromStringArray,
  orderTransformer,
  tContactInfo,
  tOption,
  tOrder,
  zContactInfo,
  zOrder,
} from './zod';
import { useAppInit } from './appInitContext';
import { useSettings } from './settingsContext';
import { ReturnFormsResponse } from '../hooks/useReturnForms';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { EASYCOM_ROUTES } from '../navigation';

const zQueryInput = z.object({
  orderNumbers: z.string().array(),
  orderLines: z.string().array().optional(),
  customerNumber: z.string().optional(),
  email: z.string().optional().optional(),
});

const zContext = z.object({
  getOrder: z.function().args(zQueryInput).returns(z.promise(zOrder.array())),
  loading: z.boolean(),
  orders: zOrder.array().optional(),
  customerOrderNumbers: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .array()
    .nullable(),
  search: z.string(),
  setSearch: z.function().args(z.string()).returns(z.void()),
  setDefaultOrder: z.function().args(z.string()).returns(z.void()),
  defaultOrder: z.string(),
  defaultCustomerNumber: z.string(),
  defaultShippingContact: zContactInfo,
});

const getOrderLines = (orderLines) => {
  if (orderLines) {
    return formatOrderNumbers(orderLines, true);
  }
  return '';
};

const getCustomerOrEmailParams = (customerNumber, email) => {
  let result = '';
  if (customerNumber && email) {
    result = `customerNumber=${customerNumber}&email=${email}`;
  } else if (customerNumber) {
    result = `customerNumber=${customerNumber}`;
  } else if (email) {
    result = `email=${email}`;
  }
  return result;
};

type tContext = z.infer<typeof zContext> & { returnForms: ReturnFormsResponse[] };
type tQueryInput = z.infer<typeof zQueryInput>;

const OrderContext = createContext<tContext | undefined>(undefined);

export const OrderProvider: FC = ({ children }) => {
  const api = useApi();
  const { params } = useAppInit();
  const { digitalReturnSettingsId } = useSettings();
  const navigate = useAppNavigate();
  const location = useLocation();

  const {
    customerNumber: initCustomerNumber,
    email: initEmail,
    initOrderKeys,
    initOrderLines,
    allowOrderFiltering,
    allowOrderRowFiltering,
    returnType,
    filterBy,
  } = params;

  const [orders, setOrders] = useState<tOrder[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(undefined);
  const [defaultOrder, setDefaultOrder] = useState<string | undefined>(
    initOrderKeys && initOrderKeys[0] ? initOrderKeys[0] : undefined,
  );
  const [defaultCustomerNumber, setDefaultCustomerNumber] = useState<
    string | undefined
  >();
  const [customerOrderNumbers, setCustomerOrderNumbers] = useState<
    tOption[] | undefined
  >(undefined);
  const [defaultShippingContact, setDefaultShippingContact] = useState<
    tContactInfo | undefined
  >(undefined);
  const [returnForms, setReturnForms] = useState<ReturnFormsResponse[]>([]);

  const getOrder = useCallback(
    async ({
      orderNumbers,
      orderLines,
      email,
      customerNumber,
    }: tQueryInput) => {
      setLoading(true);

      const response = await api(
        `digitalorders?${getCustomerOrEmailParams(
          customerNumber,
          email,
        )}&allowOrderFiltering=${allowOrderFiltering}&allowOrderRowFiltering=${allowOrderRowFiltering}&orders=${formatOrderNumbers(
          orderNumbers,
          false,
        )}${getOrderLines(orderLines)}&returnType=${returnType}${(filterBy ? `&filterBy=${filterBy}` : '')}`,
        'GET',
        'no-cors',
      );

      if (response.status === 'Failure') {
        setLoading(false);

        if (location.pathname !== '/login') {
          navigate(EASYCOM_ROUTES.LOGIN);
        }

        return response;
      }

      const returnFormsResponse = await api(
        `returnforms?digitalReturnSettingsId=${digitalReturnSettingsId}`,
        'GET',
        'no-cors',
      );

      if (returnFormsResponse.status !== 'Failure') {
        setReturnForms(returnFormsResponse as ReturnFormsResponse[]);
        setLoading(false);
      }

      if (response.data.customerOrderNumbers) {
        setCustomerOrderNumbers(
          getValueLabelFromStringArray(response.data.customerOrderNumbers),
        );
        setDefaultOrder(response.data.orders[0].orderNumber);
      } else {
        setCustomerOrderNumbers(
          getValueLabelFromStringArray([response.data.orders[0].orderNumber]),
        );
        setDefaultOrder(response.data.orders[0].orderNumber);
      }

      if (!defaultShippingContact) {
        const {
          addressLine1,
          addressLine2,
          addressLine3,
          addressLine4,
          city,
          region,
          district,
          postalCode,
          country,
        } = response.data.orders[0].shippingAddress;

        setDefaultShippingContact({
          firstName: null,
          lastName: null,
          email: null,
          phoneNumber: null,
          addressLine1,
          addressLine2,
          addressLine3,
          addressLine4,
          city,
          region,
          district,
          postalCode,
          country,
        });
      }

      const result = orderTransformer(response.data);
      if (!defaultCustomerNumber && result[0] && result[0].customerNumber) {
        setDefaultCustomerNumber(result[0].customerNumber);
      }
      setOrders(result);
      setLoading(false);
      return result;
    },
    [
      digitalReturnSettingsId,
      returnType,
      defaultCustomerNumber,
      defaultShippingContact,
      allowOrderFiltering,
      allowOrderRowFiltering,
      filterBy,
      api,
      navigate,
      location,
    ],
  );

  useEffect(() => {
    if (initOrderKeys && !orders) {
      getOrder({
        orderNumbers: initOrderKeys,
        orderLines: initOrderLines,
        email: initEmail,
        customerNumber: initCustomerNumber,
        filterBy,
      });
    }
  }, []);

  const currentContext = useMemo(
    () => ({
      orders,
      getOrder,
      loading,
      defaultOrder,
      defaultCustomerNumber,
      customerOrderNumbers,
      search,
      setSearch,
      setDefaultOrder,
      defaultShippingContact,
      setDefaultShippingContact,
      returnForms,
    }),
    [
      search,
      loading,
      orders,
      customerOrderNumbers,
      getOrder,
      setDefaultOrder,
      defaultOrder,
      defaultCustomerNumber,
      defaultShippingContact,
      setDefaultShippingContact,
      returnForms,
    ],
  );

  OrderContext.displayName = 'OrderProvider';
  return (
    <OrderContext.Provider value={currentContext}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => useRequiredContext(OrderContext);
